import { type, weight } from './tokens';

// ==============================|| DEFAULT THEME - TYPOGRAPHY ||============================== //
//
// This is the ONLY type scale in the app. It is intentionally not duplicated by
// a MuiTypography styleOverride in compStyleOverride.js - a component override
// wins over these variant styles, so declaring sizes in both places makes one
// of them unreachable.

const Typography = (theme, borderRadius, fontFamily) => ({
    fontFamily,
    h6: {
        fontSize: type.h6,
        fontWeight: weight.semibold,
        lineHeight: 1.45,
        color: theme.palette.grey[600]
    },
    h5: {
        fontSize: type.h5,
        fontWeight: weight.semibold,
        lineHeight: 1.45,
        color: theme.palette.grey[600]
    },
    h4: {
        fontSize: type.h4,
        fontWeight: weight.semibold,
        lineHeight: 1.4,
        color: theme.palette.grey[600]
    },
    h3: {
        fontSize: type.h3,
        fontWeight: weight.semibold,
        lineHeight: 1.35,
        color: theme.palette.grey[600]
    },
    h2: {
        fontSize: type.h2,
        fontWeight: weight.bold,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        color: theme.palette.grey[600]
    },
    h1: {
        fontSize: type.h1,
        fontWeight: weight.bold,
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        color: theme.palette.grey[600]
    },
    subtitle1: {
        fontSize: type.body1,
        fontWeight: weight.medium,
        lineHeight: 1.5,
        color: theme.palette.text.dark
    },
    subtitle2: {
        fontSize: type.body2,
        fontWeight: weight.regular,
        lineHeight: 1.5,
        color: theme.palette.text.secondary
    },
    caption: {
        fontSize: type.caption,
        fontWeight: weight.regular,
        lineHeight: 1.5,
        color: theme.palette.text.secondary
    },
    overline: {
        fontSize: type.overline,
        fontWeight: weight.semibold,
        lineHeight: 1.5,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary
    },
    body1: {
        fontSize: type.body1,
        fontWeight: weight.regular,
        lineHeight: 1.55
    },
    body2: {
        fontSize: type.body2,
        fontWeight: weight.regular,
        lineHeight: 1.55,
        letterSpacing: '0em',
        color: theme.palette.text.primary
    },
    button: {
        fontSize: type.body1,
        fontWeight: weight.semibold,
        textTransform: 'none'
    },
    customInput: {
        marginTop: 1,
        marginBottom: 1,
        '& > label': {
            top: 23,
            left: 0,
            color: theme.palette.grey[500],
            '&[data-shrink="false"]': {
                top: 5
            }
        },
        '& > div > input': {
            padding: '30.5px 14px 11.5px !important'
        },
        '& legend': {
            display: 'none'
        },
        '& fieldset': {
            top: 0
        }
    },
    mainContent: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[100],
        width: '100%',
        // `minWidth: 0` lets this flex child shrink below the intrinsic width
        // of a wide table instead of forcing the page to scroll sideways.
        minWidth: 0,
        maxWidth: '100%',
        minHeight: 'calc(100vh - 88px)',
        flexGrow: 1,
        padding: '16px',
        marginTop: '88px',
        marginRight: '16px',
        borderRadius: `${borderRadius}px`,
        // Reclaim the gutters on a phone - 32px of margin on a 360px screen is
        // nearly 10% of the usable width.
        [theme.breakpoints.down('sm')]: {
            padding: '12px',
            marginRight: 0,
            borderRadius: 0
        }
    },
    menuCaption: {
        fontSize: type.body2,
        fontWeight: weight.semibold,
        color: theme.palette.grey[600],
        padding: '6px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginTop: '10px'
    },
    subMenuCaption: {
        fontSize: type.overline,
        fontWeight: weight.medium,
        color: theme.palette.text.secondary,
        textTransform: 'capitalize'
    },
    commonAvatar: {
        cursor: 'pointer',
        borderRadius: '8px'
    },
    smallAvatar: {
        width: '22px',
        height: '22px',
        fontSize: '1rem'
    },
    mediumAvatar: {
        width: '34px',
        height: '34px',
        fontSize: '1.2rem'
    },
    largeAvatar: {
        width: '44px',
        height: '44px',
        fontSize: '1.5rem'
    }
});

export default Typography;
